/**
 * UserMapModal.jsx
 * 
 * User-facing map modal accessible from the Header dropdown.
 * Features:
 * - Three scope options: My Submissions, Team, All
 * - Layer toggles: Offices, My, Team, All submissions
 * - Edit-only-own permission (can view all but only edit own)
 * - PBFS-themed dark map styling
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { GoogleMap, LoadScript, Marker, MarkerClusterer } from '@react-google-maps/api';
import { useAuth } from '../hooks/useAuth';
import { buildHeaders } from '../services/apiService';
import CustomDropdown from './CustomDropdown';
import AdminEditLeadModal from './admin/AdminEditLeadModal';

// PBFS palette
const PBFS = {
  green: '#0DB14B',
  navy: '#00314F',
  blue: '#235EAC',
  gray: '#939598'
};

// PBFS-themed dark map styling
const PBFS_DARK_MAP_STYLE = [
  { featureType: 'all', elementType: 'geometry', stylers: [{ color: '#1e293b' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: PBFS.navy }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#475569' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#334155' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#2f4f3a' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#1a4d2e' }] },
  { featureType: 'all', elementType: 'labels.text.fill', stylers: [{ color: '#e2e8f0' }] },
  { featureType: 'all', elementType: 'labels.text.stroke', stylers: [{ color: '#000000' }, { weight: 2 }] }
];

const DEFAULT_CENTER = { lat: 39.5, lng: -98.35 }; // Continental US center

// Generate SVG pin icons
function pinSvg({ fill, stroke, isOwn }) {
  const badge = isOwn ? '★' : '';
  const badgeY = isOwn ? 28 : 0;
  return `data:image/svg+xml,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="34" height="44" viewBox="0 0 34 44">
      <path fill="${fill}" stroke="${stroke}" stroke-width="2" d="M17 2C9.268 2 3 8.268 3 16c0 9 14 24 14 24s14-15 14-24c0-7.732-6.268-14-14-14z"/>
      <circle cx="17" cy="16" r="6" fill="${stroke}"/>
      ${badge ? `<text x="17" y="${badgeY}" text-anchor="middle" font-size="10" fill="#FFD700" font-weight="bold">${badge}</text>` : ''}
    </svg>
  `)}`;
}

// Small office marker - matches PBFS branding
function officeSvg() {
  return `data:image/svg+xml,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
      <circle cx="10" cy="10" r="8" fill="${PBFS.navy}" stroke="${PBFS.green}" stroke-width="2"/>
      <text x="10" y="14" text-anchor="middle" font-size="10" fill="${PBFS.green}" font-weight="bold">P</text>
    </svg>
  `)}`;
}

function normalizeStatus(raw) {
  const s = (raw || '').toLowerCase().trim();
  if (s === 'approved') return 'approved';
  if (s === 'rejected') return 'rejected';
  return 'pending';
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric' 
    });
  } catch {
    return dateStr;
  }
}

export default function UserMapModal({ isOpen, onClose }) {
  const { submittedOnBehalfOf } = useAuth();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

  // State - Default to ALL submissions, ALL time for full visibility
  const [scope, setScope] = useState('all'); // my | team | all
  const [days, setDays] = useState(0); // 0 = all time
  const [submissions, setSubmissions] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [mapView, setMapView] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Layer toggles - default to showing all submissions
  const [layers, setLayers] = useState({
    offices: true,
    mySubmissions: true,
    teamSubmissions: true,
    allSubmissions: true,
  });
  
  // User location state
  const [isLocating, setIsLocating] = useState(false);
  const [userLocation, setUserLocation] = useState(null);

  const mapRef = useRef(null);
  const viewInitRef = useRef(false);
  const [mapsLoaded, setMapsLoaded] = useState(false);

  // Get user email from auth context (submittedOnBehalfOf in password mode)
  const userEmail = (submittedOnBehalfOf || '').toLowerCase().trim();

  // Fetch offices (requires auth)
  useEffect(() => {
    if (!isOpen) return;
    fetch(`${backendUrl}/api/map/offices`, { headers: buildHeaders() })
      .then(r => r.json())
      .then(data => {
        if (data?.success) setOffices(data.offices || []);
      })
      .catch(e => console.error('Failed to load offices:', e));
  }, [isOpen, backendUrl]);

  // Fetch submissions based on scope (protected endpoint - requires MSAL token)
  useEffect(() => {
    if (!isOpen || !userEmail) return;
    setLoading(true);
    setError(null);
    
    fetch(`${backendUrl}/api/map/submissions?email=${encodeURIComponent(userEmail)}&scope=${scope}&days=${days}&limit=2000`, {
      headers: buildHeaders()
    })
      .then(r => {
        if (r.status === 401) {
          setError('Authentication required. Please sign in with your Microsoft account.');
          return null;
        }
        return r.json();
      })
      .then(data => {
        if (data?.success) {
          setSubmissions(data.submissions || []);
        } else if (data) {
          setError(data?.detail || 'Failed to load map data');
        }
      })
      .catch(e => {
        console.error('Map fetch error:', e);
        setError('Failed to load map data');
      })
      .finally(() => setLoading(false));
  }, [isOpen, backendUrl, userEmail, scope, days]);

  // Filter submissions based on layers
  const filtered = useMemo(() => {
    if (!submissions.length) return [];
    return submissions.filter(s => {
      const isOwn = s.is_own === true;
      if (layers.mySubmissions && isOwn) return true;
      if (layers.teamSubmissions && !isOwn && scope === 'team') return true;
      if (layers.allSubmissions && !isOwn && scope === 'all') return true;
      // If showing all scope but only "mySubmissions" layer is on, show own only
      if (scope === 'all' && layers.mySubmissions && isOwn) return true;
      if (scope === 'team' && layers.mySubmissions && isOwn) return true;
      return false;
    });
  }, [submissions, layers, scope]);

  // Counts for layer labels
  const counts = useMemo(() => {
    let my = 0, team = 0, all = 0;
    submissions.forEach(s => {
      if (s.is_own) my++;
      else all++;
    });
    team = submissions.length - my; // Others in team scope
    return { my, team: submissions.length, all: submissions.length };
  }, [submissions]);

  // Calculate initial center - DEFAULT TO USER'S LATEST SUBMISSION
  const initialCenter = useMemo(() => {
    // Find the user's most recent submission (submissions are sorted by created_at DESC from backend)
    const ownSubmissions = submissions.filter(s => s.is_own === true);
    const latest = ownSubmissions.find(s => 
      Number.isFinite(Number(s.map_latitude)) && Number.isFinite(Number(s.map_longitude))
    );
    if (latest) {
      return { lat: Number(latest.map_latitude), lng: Number(latest.map_longitude) };
    }
    // Fallback to any submission with valid coords
    const any = submissions.find(s => 
      Number.isFinite(Number(s.map_latitude)) && Number.isFinite(Number(s.map_longitude))
    );
    if (any) {
      return { lat: Number(any.map_latitude), lng: Number(any.map_longitude) };
    }
    return DEFAULT_CENTER;
  }, [submissions]);

  // Initialize view once - zoom in more for user's own submissions
  useEffect(() => {
    if (viewInitRef.current) return;
    if (!submissions.length) return;
    // Use zoom level 10 to focus on the user's latest submission area
    setMapView({ center: initialCenter, zoom: 10 });
    viewInitRef.current = true;
  }, [submissions, initialCenter]);

  // Reset view init when modal reopens
  useEffect(() => {
    if (isOpen) {
      viewInitRef.current = false;
    }
  }, [isOpen]);

  // Icon cache (these are just data URIs, no Google Maps dependency)
  const iconCache = useMemo(() => ({
    approved: { own: pinSvg({ fill: PBFS.green, stroke: '#ffffff', isOwn: true }), 
                other: pinSvg({ fill: PBFS.green, stroke: '#ffffff', isOwn: false }) },
    pending: { own: pinSvg({ fill: '#FFC107', stroke: '#ffffff', isOwn: true }), 
               other: pinSvg({ fill: '#FFC107', stroke: '#ffffff', isOwn: false }) },
    rejected: { own: pinSvg({ fill: PBFS.gray, stroke: '#ffffff', isOwn: true }), 
                other: pinSvg({ fill: PBFS.gray, stroke: '#ffffff', isOwn: false }) },
    office: officeSvg(),
  }), []);

  const mapOptions = useMemo(() => ({
    styles: PBFS_DARK_MAP_STYLE,
    disableDefaultUI: false,
    zoomControl: true,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: true,
  }), []);

  // Center on user's current location
  const centerOnMyLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }
    
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const newLocation = { lat: latitude, lng: longitude };
        setUserLocation(newLocation);
        setMapView({ center: newLocation, zoom: 14 });
        
        // Also pan the map if it's loaded
        if (mapRef.current) {
          mapRef.current.panTo(newLocation);
          mapRef.current.setZoom(14);
        }
        setIsLocating(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        setIsLocating(false);
        if (error.code === error.PERMISSION_DENIED) {
          alert('Location access denied. Please enable location permissions.');
        } else {
          alert('Unable to get your location. Please try again.');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  if (!isOpen) return null;

  // Use portal to render at document body level, escaping header's stacking context
  const mapModal = createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center px-3 py-3"
      style={{
        backgroundColor: 'rgba(0, 20, 40, 0.98)',
        paddingTop: 'max(12px, env(safe-area-inset-top, 12px))',
        paddingBottom: 'max(12px, env(safe-area-inset-bottom, 12px))',
      }}
      onClick={onClose}
    >
      <div 
        className="bg-[var(--pbfs-navy)] rounded-2xl w-full max-w-6xl shadow-2xl border border-white/10 overflow-hidden flex flex-col"
        style={{ height: '100%', maxHeight: '100%', minHeight: '400px' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header - Responsive design for mobile */}
        <div className="flex flex-wrap items-center gap-2 px-2 py-2 sm:px-3 sm:py-3 border-b border-white/10 bg-[var(--pbfs-navy)]">
          {/* Row 1: Dropdowns + Close button */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <CustomDropdown
              value={scope}
              className="flex-1"
              minWidth="90px"
              onChange={(newScope) => {
                setScope(newScope);
                // Auto-enable corresponding layer when switching scope
                setLayers(prev => ({
                  ...prev,
                  teamSubmissions: newScope === 'team' ? true : prev.teamSubmissions,
                  allSubmissions: newScope === 'all' ? true : prev.allSubmissions
                }));
              }}
              options={[
                { value: 'my', label: 'My Tags' },
                { value: 'team', label: 'Team' },
                { value: 'all', label: 'All' }
              ]}
            />
            
            <CustomDropdown
              value={days}
              className="flex-1"
              minWidth="85px"
              onChange={(newDays) => setDays(Number(newDays))}
              options={[
                { value: 7, label: '7 days' },
                { value: 30, label: '30 days' },
                { value: 90, label: '90 days' },
                { value: 0, label: 'All time' }
              ]}
            />
          </div>
          
          {/* Layer toggles + Close button */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setLayers(p => ({ ...p, offices: !p.offices }))}
              className={`w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full text-sm font-bold transition-colors shadow-lg ${
                layers.offices 
                  ? 'bg-[var(--pbfs-green)] text-white' 
                  : 'bg-white/10 text-white/50 hover:bg-white/20'
              }`}
              title={`PBFS Offices (${offices.length})`}
            >
              P
            </button>
            <button
              onClick={() => setLayers(p => ({ ...p, mySubmissions: !p.mySubmissions }))}
              className={`w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full text-lg transition-colors shadow-lg ${
                layers.mySubmissions 
                  ? 'bg-[var(--pbfs-green)] text-white' 
                  : 'bg-white/10 text-white/50 hover:bg-white/20'
              }`}
              title={`My Tags (${counts.my})`}
            >
              ★
            </button>
            {scope === 'team' && (
              <button
                onClick={() => setLayers(p => ({ 
                  ...p, 
                  teamSubmissions: !p.teamSubmissions
                }))}
                className={`w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full text-sm font-bold transition-colors shadow-lg ${
                  layers.teamSubmissions
                    ? 'bg-[var(--pbfs-blue)] text-white' 
                    : 'bg-white/10 text-white/50 hover:bg-white/20'
                }`}
                title={`Team (${counts.team - counts.my})`}
              >
                T
              </button>
            )}
            
            {/* Close button */}
            <button
              onClick={onClose}
              className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-colors ml-1"
              aria-label="Close map"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Map container - flex-1 takes remaining space */}
        <div className="relative bg-slate-800 flex-1" style={{ minHeight: '200px' }}>
          {loading && (
            <div className="absolute inset-0 bg-slate-900/90 z-10 flex items-center justify-center">
              <div className="text-white text-lg">Loading map data...</div>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 bg-slate-900/90 z-10 flex items-center justify-center">
              <div className="text-red-400 text-lg">{error}</div>
            </div>
          )}
          
          {/* Center on My Location button - positioned like Google Maps */}
          <button
            onClick={centerOnMyLocation}
            disabled={isLocating}
            className={`absolute right-3 top-20 z-20 w-10 h-10 bg-white rounded-lg shadow-lg flex items-center justify-center transition-all hover:bg-gray-100 active:scale-95 ${isLocating ? 'animate-pulse' : ''}`}
            title="Center on my location"
            style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.3)' }}
          >
            {isLocating ? (
              <div className="w-5 h-5 border-2 border-[var(--pbfs-green)] border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke={userLocation ? PBFS.green : '#666'} 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                className="w-5 h-5"
              >
                <circle cx="12" cy="12" r="3"/>
                <path d="M12 2v4M12 18v4M2 12h4M18 12h4"/>
              </svg>
            )}
          </button>
          
          <div className="h-full w-full">
            {googleMapsApiKey ? (
              <LoadScript googleMapsApiKey={googleMapsApiKey} onLoad={() => setMapsLoaded(true)}>
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                  center={mapView?.center || initialCenter}
                  zoom={mapView?.zoom || 6}
                  options={mapOptions}
                  onLoad={map => { mapRef.current = map; setMapsLoaded(true); }}
                  onClick={() => {
                    setSelected(null);
                    setSelectedOffice(null);
                  }}
                >
                  {/* User's current location marker (blue dot) */}
                  {userLocation && mapsLoaded && window.google?.maps && (
                    <Marker
                      position={userLocation}
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 8,
                        fillColor: '#4285F4',
                        fillOpacity: 1,
                        strokeColor: '#ffffff',
                        strokeWeight: 3
                      }}
                      title="Your current location"
                      zIndex={1000}
                    />
                  )}

                  {/* Office markers - small PBFS branded circles */}
                  {layers.offices && mapsLoaded && window.google?.maps && offices.map(o => (
                    <Marker
                      key={o.id}
                      position={{ lat: Number(o.latitude), lng: Number(o.longitude) }}
                      icon={{
                        url: iconCache.office,
                        scaledSize: new window.google.maps.Size(16, 16),
                        anchor: new window.google.maps.Point(8, 8)
                      }}
                      title={`PBFS: ${o.name} • ${o.full_address || ''}`}
                      onClick={(e) => {
                        e.stop(); // Prevent map click
                        setSelected(null); // Clear submission selection
                        setSelectedOffice(o); // Show office info
                      }}
                    />
                  ))}

                  {/* Submission markers with clustering */}
                  <MarkerClusterer>
                    {clusterer => (
                      <>
                        {mapsLoaded && window.google?.maps && filtered.map(s => {
                          const status = normalizeStatus(s.status || s.approval_status);
                          const isOwn = s.is_own === true;
                          const iconSet = iconCache[status];
                          const iconUrl = isOwn ? iconSet.own : iconSet.other;
                          
                          return (
                            <Marker
                              key={s._id || s.id}
                              position={{ lat: Number(s.map_latitude), lng: Number(s.map_longitude) }}
                              clusterer={clusterer}
                              icon={{
                                url: iconUrl,
                                scaledSize: new window.google.maps.Size(34, 44),
                                anchor: new window.google.maps.Point(17, 44)
                              }}
                              onClick={() => setSelected(s)}
                              title={`${s.business_name || 'Tag'} • ${status}${isOwn ? ' (yours)' : ''}`}
                            />
                          );
                        })}
                      </>
                    )}
                  </MarkerClusterer>
                </GoogleMap>
              </LoadScript>
            ) : (
              <div className="h-full flex items-center justify-center bg-slate-800">
                <p className="text-white/60">Google Maps API key not configured</p>
              </div>
            )}
          </div>

          {/* Selected pin details panel - tap card to edit (own only), tap elsewhere to dismiss */}
          {selected && (
            <div 
              className={`absolute bottom-3 left-3 right-3 lg:left-4 lg:right-auto lg:max-w-[400px] bg-[var(--pbfs-navy)]/95 backdrop-blur-md border border-white/10 rounded-xl shadow-2xl p-4 transition-all ${
                selected.is_own ? 'cursor-pointer hover:border-[var(--pbfs-green)]/50 hover:shadow-[var(--pbfs-green)]/20 active:scale-[0.98]' : ''
              }`}
              onClick={(e) => {
                e.stopPropagation(); // Don't close modal when clicking on card
                if (selected.is_own) {
                  // Open edit modal for user's own submission
                  setShowEditModal(true);
                }
              }}
            >
              <div className="flex gap-3">
                {/* Thumbnail */}
                <div className="flex-shrink-0">
                  {(selected.image_url || selected.thumbnail_url) ? (
                    <img
                      src={`${backendUrl}${selected.thumbnail_url || selected.image_url}`}
                      alt="Tag"
                      className="w-16 h-16 rounded-lg object-cover border-2 border-[var(--pbfs-green)] shadow-lg"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-white/10 border-2 border-dashed border-white/20 flex items-center justify-center">
                      <span className="text-2xl">📷</span>
                    </div>
                  )}
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  {/* Business name and star */}
                  <div className="flex items-center gap-2 mb-1">
                    {selected.is_own && (
                      <span className="text-yellow-400 text-lg" title="Your submission">★</span>
                    )}
                    <span className="text-white font-bold text-base truncate flex-1">
                      {selected.business_name || 'Unknown Business'}
                    </span>
                  </div>
                  
                  {/* Address */}
                  <div className="text-white/60 text-sm truncate">
                    {[selected.site_street_address || selected.business_address, selected.site_city, selected.site_state].filter(Boolean).join(', ') || '—'}
                  </div>
                  
                  {/* Status badges */}
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                      normalizeStatus(selected.status) === 'approved' 
                        ? 'bg-[var(--pbfs-green)]/20 text-[var(--pbfs-green)] border border-[var(--pbfs-green)]/30'
                        : normalizeStatus(selected.status) === 'rejected'
                        ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                        : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                    }`}>
                      {normalizeStatus(selected.status).charAt(0).toUpperCase() + normalizeStatus(selected.status).slice(1)}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-white/60 border border-white/5">
                      {formatDate(selected.created_at)}
                    </span>
                    {selected.duplicate_status && selected.duplicate_status !== 'none' && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-400 border border-orange-500/30">
                        {selected.duplicate_status === 'canonical' ? '✓' : '🔄'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Action button/hint */}
              {selected.is_own ? (
                <button
                  className="mt-3 w-full bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/90 text-white py-2.5 px-4 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 group active:scale-[0.98]"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowEditModal(true);
                  }}
                >
                  <span>✏️</span>
                  <span>Edit Submission</span>
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </button>
              ) : (
                <div className="mt-3 text-center text-white/40 text-xs py-1 bg-white/5 rounded-lg">
                  👁️ View only • Not your submission
                </div>
              )}
            </div>
          )}

          {/* PBFS Office Info Panel - click to dismiss */}
          {selectedOffice && (
            <div 
              className="absolute bottom-3 left-3 right-3 lg:left-4 lg:right-auto lg:max-w-[400px] bg-[var(--pbfs-navy)]/95 backdrop-blur-md border-2 border-[var(--pbfs-green)] rounded-xl shadow-2xl p-4 cursor-pointer hover:border-[var(--pbfs-green)]/70 active:scale-[0.98] transition-all"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedOffice(null);
              }}
            >
              <div className="flex items-start gap-3">
                {/* Icon */}
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[var(--pbfs-green)] flex items-center justify-center shadow-lg">
                  <span className="text-white text-2xl font-bold">P</span>
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  {/* Office name */}
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white font-bold text-base">
                      {selectedOffice.name || 'PBFS Office'}
                    </span>
                  </div>
                  
                  {/* Address */}
                  {selectedOffice.full_address && (
                    <div className="text-white/70 text-sm mb-1">
                      📍 {selectedOffice.full_address}
                    </div>
                  )}
                  
                  {/* Phone */}
                  {selectedOffice.phone && (
                    <div className="text-white/70 text-sm">
                      📞 {selectedOffice.phone}
                    </div>
                  )}
                  
                  {/* Region badge if available */}
                  {selectedOffice.region && (
                    <div className="mt-2">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--pbfs-green)]/20 text-[var(--pbfs-green)] border border-[var(--pbfs-green)]/30 font-semibold">
                        {selectedOffice.region}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Tap to dismiss hint */}
              <div className="mt-3 text-center text-white/40 text-xs py-1 bg-white/5 rounded-lg">
                Tap to dismiss
              </div>
            </div>
          )}
        </div>

        {/* Footer - minimal */}
        <div className="flex items-center justify-center px-3 py-1.5 border-t border-white/10 bg-white/5">
          <div className="text-white/50 text-[10px]">
            {filtered.length} of {submissions.length} • <span className="text-yellow-400">★</span> yours • tap to edit
          </div>
        </div>
      </div>
      
    </div>,
    document.body
  );

  // Render AdminEditLeadModal - more robust than EditSubmissionModal
  // Uses admin endpoint which has proven reliability
  const editModal = selected && selected.is_own && showEditModal ? createPortal(
    <AdminEditLeadModal
      isOpen={true}
      inspection={selected}
      backendUrl={backendUrl}
      onClose={() => {
        setShowEditModal(false);
        setSelected(null); // Clear selection when closing edit
      }}
      onSaved={(updatedSubmission) => {
        // Update the submission in our local state
        setSubmissions(prev => prev.map(s => 
          (s._id || s.id) === (updatedSubmission._id || updatedSubmission.id) 
            ? updatedSubmission 
            : s
        ));
        setShowEditModal(false);
        setSelected(null);
      }}
    />,
    document.body
  ) : null;

  return (
    <>
      {mapModal}
      {editModal}
    </>
  );
}
